import React, { useEffect, useState } from 'react';

import { Button } from '@/design-system/button';
import { FormModal } from '@/design-system/form-modal';
import { Textarea } from '@/design-system/textarea';
import { toast } from '@/design-system/toaster';
import { Invoice, invoicesAPI } from '@/services/api/invoices';

interface ResubmitModalProps {
  isOpen: boolean;
  invoice: Invoice | null;
  onClose: () => void;
  onUpdated: (invoice: Invoice) => void;
}

const ResubmitModal: React.FC<ResubmitModalProps> = ({
  isOpen,
  invoice,
  onClose,
  onUpdated,
}) => {
  const [note, setNote] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setNote('');
      setIsSubmitting(false);
    }
  }, [isOpen]);

  if (!isOpen || !invoice) return null;

  const canSubmit = note.trim().length > 0 && !isSubmitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setIsSubmitting(true);
    try {
      const updated = await invoicesAPI.resubmit(invoice.id, note.trim());
      toast.success('Resubmitted for confirmation', {
        description: `${updated.invoiceNumber} is back to awaiting client.`,
      });
      onUpdated(updated);
      onClose();
    } catch (err) {
      toast.error('Could not resubmit invoice', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <FormModal
      open={isOpen}
      onClose={onClose}
      icon={<i className="ri-refresh-line" />}
      iconTone="info"
      title="Resubmit after dispute fix"
      subtitle={`${invoice.invoiceNumber} · ${invoice.client.name}`}
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button tone="neutral" style="stroke" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit}
            loading={isSubmitting}
          >
            {isSubmitting ? 'Resubmitting…' : 'Resubmit'}
          </Button>
        </div>
      }
    >
      <div>
        <label
          htmlFor="resubmit-note"
          className="mb-2 block text-[10px] font-bold uppercase tracking-[0.18em] text-text-sub-600"
        >
          Note <span className="text-error-base">*</span>
        </label>
        <Textarea
          id="resubmit-note"
          rows={4}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="What changed since the dispute?"
        />
        <p className="mt-2 text-xs text-text-sub-600">
          Previous dispute note will be replaced. Full history stays in the
          audit trail.
        </p>
      </div>
    </FormModal>
  );
};

export default ResubmitModal;
